import { add, sub } from 'date-fns';
import AddressHistory, { AddressHistoryUiModel } from '../models/be/AddressHistory';
import { getLocaleDateStringInIsoFormat, hasValue, getFormattedDate } from '../lib/utils';
import { getCountryName, getFullCountryList } from './countryList';

export const getAddressString = (addressHistory: AddressHistory) => {
	const { suburb, state, country, postcode } = addressHistory;
	const countryName = getCountryName(country);
	return `${getAddressLineString(addressHistory)}, ${suburb} ${state} ${postcode} ${countryName}`;
};

export const getAddressLineString = (addressHistory: AddressHistory) => {
	return `${hasValue(addressHistory.unitNumber) ? `${addressHistory.unitNumber}/` : ''}${
		addressHistory.buildingName ?? ''
	} ${addressHistory.streetNumber ?? ''} ${addressHistory.streetName}`;
};

export const getNoAddressMessage = (numberOfYearsToCover: number) =>
	numberOfYearsToCover > 0
		? `Your address history must cover at least the past ${numberOfYearsToCover} years without any gaps, with your current address listed up to today's date.`
		: `Please supply your current residential address. If you don't remember the exact dates, please be as accurate as possible.`;

export const getNoAddressContent = (numberOfYearsToCover: number) =>
	numberOfYearsToCover > 0
		? `Please provide your residential addresses for the past ${numberOfYearsToCover} years, ensuring there are no gaps or overlaps. If you are unsure of exact dates, provide the most accurate information possible. Your current address should be listed up to today's date.
		
		If addresses, including flat & house numbers, are not added exactly as listed with the local Post service, the check could be withdrawn or cause significant delays.`
		: `Please supply your current residential address. If you don't remember the exact dates, please be as accurate as possible.`;

// This has been duplicated to Validation.cs.
export const validateAddressHistoryEditor = (
	descendingAddressHistories: AddressHistory[],
	numberOfYearsToCover: number,
	countryRestriction?: string,
	validationType?: string,
	requiredCountry?: string
) => {
	let message = null;

	if (hasValue(countryRestriction)) {
		message = ValidateCountryRestriction(descendingAddressHistories, countryRestriction);
		if (message) return message;
	}

	// For USCR checks, validate that at least one US address exists
	if (validationType === 'requireUsAddress' || (hasValue(requiredCountry) && requiredCountry !== countryRestriction)) {
		message = ValidateInternationalCheckCountryRequirement(descendingAddressHistories, requiredCountry);
		if (message) return message;
	}

	if (descendingAddressHistories?.length === 0) {
		message = getNoAddressMessage(numberOfYearsToCover);
	} else if (!isToDateEqualToToday(descendingAddressHistories) && numberOfYearsToCover !== 0) {
		message = 'Please update your address history end date.';
	} else if (checkBuildNameOrStreetNumberPresent(descendingAddressHistories)) {
		message = 'Building Name or Street Number is required';
	} else if (
		numberOfYearsToCover !== 0 &&
		!validateDateRangeContinuity(descendingAddressHistories, numberOfYearsToCover)
	) {
		const dateRangeGapWarningMessage = `Please include at least ${numberOfYearsToCover} years of uninterrupted address history up to today.`;
		message = dateRangeGapWarningMessage;
	}
	return message;
};

export const validateAddressHistoriesWithSelectedIndex =
	(addressHistories: AddressHistory[], selectedIndex: number, ignoreAddressRange?: boolean) =>
	(values: { addressHistory: AddressHistory }) => {
		const { fromDate, toDate, buildingName, streetNumber } = values.addressHistory;
		if (!ignoreAddressRange && hasValue(fromDate) && hasValue(toDate) && new Date(fromDate) > new Date(toDate)) {
			return 'To date must not be before From date.';
		}
		if (
			!ignoreAddressRange &&
			!validateDateRangeOverlapping(
				generateUpdatedAscendingAddressHistories(addressHistories, values.addressHistory, selectedIndex)
			)
		) {
			return 'The date range overlaps other addresses.';
		}

		if (!hasValue(buildingName) && !hasValue(streetNumber)) return 'Building Name or Street Number is required';

		return null;
	};

export const ValidateCountryRestriction = (addressHistories: AddressHistory[], countryRestriction: string) => {
	// Check if there are addresses outside the restricted country
	if (addressHistories.filter((address) => address.country !== countryRestriction).length > 0) {
		const countryName = getFullCountryList().find((c) => c.value === countryRestriction).text;
		return `Please only add addresses within ${countryName}.`;
	}
	return null;
};

export const ValidateInternationalCheckCountryRequirement = (
	addressHistories: AddressHistory[],
	requiredCountry: string
) => {
	// Check if at least one address from the required country exists
	const hasRequiredCountryAddress = addressHistories.some((address) => address.country === requiredCountry);
	if (!hasRequiredCountryAddress) {
		const countryName = getFullCountryList().find((c) => c.value === requiredCountry)?.text || requiredCountry;
		return `At least one address from ${countryName} is required.`;
	}
	return null;
};

const validateDateRangeOverlapping = (addressHistories: AddressHistory[]) => {
	const ascendingAddressHistories = getAscendingDateRangeAddressHistories(addressHistories);

	if (ascendingAddressHistories.length === 1) return true;

	const overlappedDateRange = ascendingAddressHistories.find((x, i, array) => {
		if (i === 0) {
			return false;
		}
		const previous = array[i - 1];
		const current = array[i];
		return previous.to >= current.from;
	});
	return overlappedDateRange === undefined;
};

// This has been duplicated to Validation.cs.
const validateDateRangeContinuity = (addressHistories: AddressHistory[], numberOfYearsToCover: number) => {
	if (addressHistories.length === 0) return false;

	if (addressHistories[0].fromDate === null || addressHistories[0].toDate === null) return false;

	const descendingAddressHistories = getAscendingDateRangeAddressHistories(addressHistories).reverse();
	const mininumYearsInMilliseconds = numberOfYearsToCover * 365 * 24 * 60 * 60 * 1000;
	const oneDayInMilliseconds = 24 * 60 * 60 * 1000;
	const twoDaysInMilliseconds = 24 * 60 * 60 * 1000 * 2;
	const latestToDateInEpochMilliseconds = descendingAddressHistories[0].to.getTime();

	const isPresentHistoryUpToDate = new Date().getTime() - latestToDateInEpochMilliseconds < twoDaysInMilliseconds;
	if (!isPresentHistoryUpToDate) return false;

	const hasRequiredYearsForPresentHistory =
		latestToDateInEpochMilliseconds - descendingAddressHistories[0].from.getTime() >= mininumYearsInMilliseconds;
	if (hasRequiredYearsForPresentHistory) return true;

	for (let i = 1; i < descendingAddressHistories.length; i++) {
		const previous = descendingAddressHistories[i - 1];
		const current = descendingAddressHistories[i];
		const hasGapWithPreviousHistory = previous.from.getTime() - current.to.getTime() > oneDayInMilliseconds;
		const hasRequiredYearsOfHistory =
			latestToDateInEpochMilliseconds - current.from.getTime() >= mininumYearsInMilliseconds;
		if (hasGapWithPreviousHistory) {
			return false;
		}
		if (hasRequiredYearsOfHistory) {
			return true;
		}
	}

	return false;
};

// This has been duplicated to Validation.cs.
const isToDateEqualToToday = (descendingAddressHistories: AddressHistory[]) => {
	const recentAddress = descendingAddressHistories[0];
	const { toDate } = recentAddress;
	const twoDaysAfter = getLocaleDateStringInIsoFormat(add(new Date(), { days: 2 }));
	const twoDaysBefore = getLocaleDateStringInIsoFormat(sub(new Date(), { days: 2 }));

	return toDate <= twoDaysAfter && toDate >= twoDaysBefore;
};

const checkBuildNameOrStreetNumberPresent = (addressHistories: AddressHistory[]): boolean => {
	return addressHistories.some((history) => !hasValue(history.buildingName) && !hasValue(history.streetNumber));
};

export const generateUpdatedAscendingAddressHistories = (
	addressHistories: AddressHistory[],
	newAddressHistory: AddressHistory,
	selectedIndex: number,
	numberOfYearsToCover?: number
) => {
	const newAddress = { ...newAddressHistory };
	newAddress.postcode = newAddress.postcode.toUpperCase();
	if (numberOfYearsToCover === 0) {
		newAddress.fromDate = newAddress.fromDate ?? new Date().toDateString();
		newAddress.toDate = newAddress.toDate ?? new Date().toDateString();
	}
	let updatedAddressHistories = addressHistories;
	if (selectedIndex === null) {
		if (addressHistories) updatedAddressHistories = addressHistories!.concat([newAddress]);
		else updatedAddressHistories = [newAddress];
	} else {
		updatedAddressHistories = [...addressHistories];
		updatedAddressHistories[selectedIndex] = newAddress;
	}
	return getAscendingAddressHistories(updatedAddressHistories);
};

// This has been duplicated to Validation.cs.
export const getAscendingDateRangeAddressHistories = (addressHistories: AddressHistory[]) => {
	if (addressHistories?.find((x) => x.fromDate === null)) throw new Error('Address History date should not be null.');

	const dateRangeArray: AddressHistoryUiModel[] = addressHistories?.map((x) => {
		return {
			from: new Date(x.fromDate),
			to: new Date(x.toDate),
			...x
		};
	});

	if (dateRangeArray?.length <= 1) return dateRangeArray;

	return dateRangeArray?.sort((a, b) => a.from.getTime() - b.from.getTime());
};

export const getAscendingAddressHistories = (addressHistories: AddressHistory[]) => {
	const ascendingDateRangeArray = getAscendingDateRangeAddressHistories(addressHistories);
	return ascendingDateRangeArray.map((x: AddressHistoryUiModel) => ({
		buildingName: x.buildingName,
		country: x.country,
		fromDate: x.fromDate,
		locality: x.locality,
		postcode: x.postcode,
		state: x.state,
		streetName: x.streetName,
		streetNumber: x.streetNumber,
		suburb: x.suburb,
		toDate: x.toDate,
		unitNumber: x.unitNumber
	}));
};

export const buildAddressDateString = (address: AddressHistory) => {
	const { fromDate, toDate } = address;
	const from = fromDate ? `from ${getFormattedDate(fromDate)}` : '';
	const to = toDate ? ` to ${getFormattedDate(toDate)}` : '';
	return `(${from}${to})`;
};
